import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminShell } from "./AdminShell";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, KeyRound, Eye, EyeOff } from "lucide-react";

const AdminChangePassword = () => {
    const navigate = useNavigate();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [saving, setSaving] = useState(false);

    const canSubmit =
        current.length >= 1 &&
        next.length >= 6 &&
        confirm === next;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSaving(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password changed successfully");
            setCurrent(""); setNext(""); setConfirm("");
            navigate("/admin");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Change failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <AdminShell title="Change Password">
            <div className="max-w-md mx-auto">
                <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-xl bg-emerald-600 text-white grid place-items-center shadow-md">
                        <KeyRound className="h-6 w-6" />
                    </div>
                    <div>
                        <h2 className="font-display font-bold text-xl text-blue-950 tracking-tight">
                            Change your admin password
                        </h2>
                        <p className="text-xs text-slate-500 mt-0.5">Naya password kam se kam 6 characters ka rakho.</p>
                    </div>
                </div>

                <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="admin-change-password-form">
                    <Field
                        label="Current password"
                        testid="cp-current"
                        value={current}
                        onChange={setCurrent}
                        show={showCurrent}
                        onToggle={() => setShowCurrent((s) => !s)}
                    />
                    <Field
                        label="New password"
                        testid="cp-new"
                        value={next}
                        onChange={setNext}
                        show={showNext}
                        onToggle={() => setShowNext((s) => !s)}
                        hint={next && next.length < 6 ? "Kam se kam 6 characters chahiye" : null}
                    />
                    <Field
                        label="Confirm new password"
                        testid="cp-confirm"
                        value={confirm}
                        onChange={setConfirm}
                        show={showConfirm}
                        onToggle={() => setShowConfirm((s) => !s)}
                        hint={confirm && confirm !== next ? "Password match nahi ho raha" : null}
                    />

                    <button
                        type="submit"
                        disabled={!canSubmit || saving}
                        data-testid="cp-submit"
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-blue-700 text-white text-sm font-semibold hover:bg-blue-800 disabled:opacity-50"
                    >
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Save New Password
                    </button>
                </form>
            </div>
        </AdminShell>
    );
};

const Field = ({ label, testid, value, onChange, show, onToggle, hint }) => (
    <div>
        <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            {label}
        </label>
        <div className="mt-1 relative">
            <input
                type={show ? "text" : "password"}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                autoComplete="new-password"
                className="w-full border border-slate-300 rounded-md pl-3 pr-9 py-2 text-sm focus:border-blue-800 outline-none"
            />
            <button
                type="button"
                onClick={onToggle}
                data-testid={`${testid}-toggle`}
                className="absolute inset-y-0 right-0 px-2 grid place-items-center text-slate-400 hover:text-slate-700"
                tabIndex={-1}
            >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
        </div>
        {hint && <div className="text-[11px] text-red-600 mt-1">{hint}</div>}
    </div>
);

export default AdminChangePassword;
